import type { AffectedKind, ChangeConsequences } from './consequences.ts';

/**
 * What a researcher is told a change will do below it, before it commits: on the step, as a notice
 * of what a change there would affect, and in the confirm a change has to pass. The server's
 * `change_unacknowledged` refusal says the same, so the agent can put it in plain words. Every
 * sentence is kept in this one module, so a change of wording is a change to one file.
 */

/** Each step as a sentence names it, and whether it takes a plural verb. */
const NAMES: Record<AffectedKind, { name: string; plural: boolean }> = {
  materials: { name: 'materials', plural: true },
  goal: { name: 'goal options', plural: true },
  rqs: { name: 'questions', plural: true },
  dg: { name: 'guide', plural: false },
  audience: { name: 'audience', plural: false },
};

/** "guide", "guide and audience", "questions, guide and audience". */
const listed = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names.at(-1) as string}`;

/** The steps named, with the verb that agrees with them: "the guide is", "the questions are". */
const subject = (kinds: readonly AffectedKind[]): string => {
  const plural = kinds.length > 1 || kinds.some((kind) => NAMES[kind].plural);

  return `${listed(kinds.map((kind) => NAMES[kind].name))} ${plural ? 'are' : 'is'}`;
};

/** What the change does below it, as one sentence; empty when nothing below it is affected. */
const changeSentence = (consequences: ChangeConsequences): string => {
  const kinds = consequences.affected.map((step) => step.kind);
  const after = kinds.filter((kind) => kind !== 'materials');

  switch (consequences.changing) {
    case 'study_type': {
      if (!kinds.includes('materials')) {
        return after.length === 0 ? '' : `Change the type and your ${subject(after)} updated.`;
      }

      return after.length === 0
        ? 'Change the type and you’ll re-check your materials.'
        : `Change the type and you’ll re-check your materials; the ${subject(after)} updated after that.`;
    }
    case 'materials':
      return kinds.length === 0
        ? ''
        : `Save this and your ${subject(kinds)} updated after you confirm the materials.`;
    case 'goal':
      return kinds.length === 0
        ? ''
        : `Pick a different goal and the ${subject(kinds)} redrafted after you approve.`;
    case 'rqs':
    case 'dg':
    case 'audience':
      return kinds.length === 0 ? '' : `Change this and the ${subject(kinds)} updated to match.`;
  }
};

/** The images a Materials change takes off the guide, by the names the researcher gave them. */
export const imagesRemovedText = (consequences: ChangeConsequences): string => {
  const names = consequences.imagesRemoved.map((image) => image.name);
  if (names.length === 0) {
    return '';
  }

  return names.length === 1
    ? `This image leaves the guide: ${names[0] as string}.`
    : `These images leave the guide: ${listed(names)}.`;
};

/** Said when the change takes a ready study back to draft. */
export const LAUNCH_WAITS = 'Launch waits until you approve them again.';

/**
 * Everything a change will do below it, in the order it is said: what it updates, the images it
 * takes off the guide, and that launch waits when the study is ready now. Empty when the change
 * affects nothing below it.
 */
export const consequencesText = (consequences: ChangeConsequences): string => {
  const sentences = [changeSentence(consequences), imagesRemovedText(consequences)].filter(
    (sentence) => sentence !== '',
  );
  if (sentences.length === 0) {
    return '';
  }

  return [...sentences, ...(consequences.leavesReady ? [LAUNCH_WAITS] : [])].join(' ');
};

/** The button that makes an acknowledged change, and the one that leaves things as they are. */
export const CONFIRM_CHANGE = 'Make the change';
export const CANCEL_CHANGE = 'Keep it as it is';
